import { PUBLIC_SITE_DOMAIN } from '$app/env/public';
import type { Game } from '~/store/game.svelte';
import { DailyGame } from '~/store/daily.svelte';
import { isDaily, type Match } from '~/types';

export const getImage = async (url: string) => {
  const res = await fetch(url);
  const data = await res.blob();
  return data;
};

const letterMap: Record<string, string> = {
  A: '🇦',
  B: '🇧',
  C: '🇨',
  D: '🇩',
  E: '🇪',
  F: '🇫',
  G: '🇬',
  H: '🇭',
  I: '🇮',
  J: '🇯',
  K: '🇰',
  L: '🇱',
  M: '🇲',
  N: '🇳',
  O: '🇴',
  P: '🇵',
  Q: '🇶',
  R: '🇷',
  S: '🇸',
  T: '🇹',
  U: '🇺',
  V: '🇻',
  W: '🇼',
  X: '🇽',
  Y: '🇾',
  Z: '🇿',
};

const numberMap = ['0️⃣', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣'];

const clockMap = [
  '🕛',
  '🕐',
  '🕑',
  '🕒',
  '🕓',
  '🕔',
  '🕕',
  '🕖',
  '🕗',
  '🕘',
  '🕙',
  '🕚',
];

const circle = '⚪';

export const getShareText = (game: Game, topWords: Match[]) => {
  let text = 'GRAM JAM\n';
  if (isDaily(game)) {
    text += getDailyShareText(game as DailyGame, topWords);
  } else {
    text += getClassicShareText(topWords);
  }
  text += '\n' + PUBLIC_SITE_DOMAIN;
  return text;
};

const getNumberText = (n: number, max: number) => {
  const s = n.toString().split('');
  let text = '';
  for (let i = max.toString().length; i > 0; i--) {
    if (i > s.length) {
      text += numberMap[0];
    } else {
      text += numberMap[+s.shift()!];
    }
  }
  return text;
};

export const getDailyShareText = (game: DailyGame, words: Match[]) => {
  const maxSwaps = Math.max(...words.map((w) => w.swaps ?? 0));
  const stars = Array.from({ length: game.stars ?? 0 })
    .map((_) => '⭐')
    .join(' ');
  let text = `Stars: ${stars || '❌'}\n\n`;
  text += `⏲️ Swaps: ${game.turns}\n`;
  text += '➖➖➖➖➖➖\n';
  let totalSwaps = 0;
  for (const word of words) {
    const swaps = word.swaps ?? 0;
    totalSwaps += swaps;
    text += getNumberText(swaps, maxSwaps) + ' ';
    text += swaps === 0 ? circle : clockMap[totalSwaps % 12];
    text += ' ';
    text += Array.from({ length: word.word.length }).map((_) => word.axis === 'col' ? '🟦' : '🟧').join('') + '\n';
  }
  const leftovers = game.finalBoard?.flat();
  if (leftovers?.length) {
    text += `leftovers: ${leftovers.map((t) => letterMap[t.letter] + '​').join('')}\n`;
  } else {
    text += '⭐ All Clear!\n';
  }
  return text;
};

export const getClassicShareText = (words: Match[]) => {
  let text = '📘 Best Words:\n';
  // text += '--------------\n';
  text += '➖➖➖➖➖➖\n';
  const maxScore = words[0]?.score ?? 0;
  for (const word of words) {
    const letters = word.word
      .map((t) => t.letter)
      .join('')
      .split('');
    text += getNumberText(word.score, maxScore) + '  ';
    for (const letter of letters) {
      text += letterMap[letter] + '​';
    }
    text += '\n';
  }
  return text;
};

export const copyShareText = (text: string) => {
  const copyArea = document.createElement('textarea');
  copyArea.textContent = text;
  document.body.appendChild(copyArea);

  const selection = document.getSelection() as Selection;
  const range = document.createRange();
  //  range.selectNodeContents(textarea);
  range.selectNode(copyArea);
  selection.removeAllRanges();
  selection.addRange(range);
  document.execCommand('copy');
  selection.removeAllRanges();
  document.body.removeChild(copyArea);
};

export const copyShareImage = async (blob: Blob) => {
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'image/png': blob,
      }),
    ]);
  } catch (err) {
    console.error(err);
    const img = document.createElement('img');
    const urlCreator = window.URL || window.webkitURL;
    img.src = urlCreator.createObjectURL(blob);

    const div = document.createElement('div');
    div.contentEditable = 'true';
    div.appendChild(img);
    document.body.appendChild(div);

    const selection = document.getSelection() as Selection;
    selection.removeAllRanges();
    let range = document.createRange();
    range.selectNode(div);
    selection.addRange(range);
    document.execCommand('copy');
    selection.removeAllRanges();
    document.body.removeChild(div);
  }
};
